import { ChevronUpIcon } from "@chakra-ui/icons";
import {
  Breadcrumb,
  BreadcrumbItem,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  Box,
  AccordionIcon,
  AccordionPanel,
  BreadcrumbLink,
  SkeletonText,
  Divider,
  HStack,
} from "@chakra-ui/react";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { CopyText } from "../../components/Utilities/CopyText";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import DateUtil from "../../util/DateUtil";

export const MemberDetail = () => {
  const { memberId } = useParams();
  const { data: member, isLoading } = useAdminApiRequest(
    (apiClient) => apiClient.getMemberDetail(memberId!),
    [memberId]
  );
  return (
    <>
      <PageHeading>
        <PageHeading.Title>Member Detail</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/admin/members`}>
                Members
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/admin/members/${memberId}`}>
                {member?.FirstName} {member?.LastName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          {isLoading && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <SkeletonText w="220px" noOfLines={20} spacing="4" />
              <SkeletonText w="800px" noOfLines={2} spacing="4" />
            </VStack>
          )}
          {!isLoading && member && (
            <>
              <HStack w="100%">
                <Box pb={4}>
                  <Text fontSize="md" color="cherryUi.600">
                    {member?.Meta?.Id} / {member?.MembershipNumber}
                  </Text>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>
                      {member?.FirstName} {member?.LastName}
                    </b>
                  </Text>
                </Box>
              </HStack>
              <VStack alignItems="flex-start" w="100%" pb={6}>
                <ViewControl label="Title" value={member.Title ?? "-"} />
                <ViewControl
                  label="Member Key"
                  value={member.MemberKey ?? "-"}
                />
                <ViewControl
                  label="Membership Number"
                  value={member.MembershipNumber ?? "-"}
                />
                <ViewControl label="Business Id">
                  <Box flex={3}>
                    {(member.BusinessId && (
                      <>
                        <PreviewButton
                          url={`/admin/businesses/${member.BusinessId}`}
                        >
                          {member.BusinessId}
                        </PreviewButton>
                        <CopyText ml={1} text={member.BusinessId}>
                          Copy
                        </CopyText>
                      </>
                    )) ||
                      "-"}
                  </Box>
                </ViewControl>
                <ViewControl
                  label="First Name"
                  value={member.FirstName ?? "-"}
                />
                <ViewControl
                  label="Middle Name"
                  value={member.MiddleName ?? "-"}
                />
                <ViewControl label="Last Name" value={member.LastName ?? "-"} />
                <ViewControl
                  label="Preferred Name"
                  value={member.PreferredName ?? "-"}
                />
                <ViewControl
                  label="Birth Date"
                  value={
                    member.BirthDate
                      ? DateUtil.formatDate(member.BirthDate, "en-AU", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "-"
                  }
                />
                <ViewControl
                  label="Financial"
                  value={member.Financial ? "Yes" : "No"}
                />
                <ViewControl
                  label="Nationality"
                  value={member.Nationality ?? "-"}
                />
                <ViewControl label="Gender" value={member.Gender ?? "-"} />
                <ViewControl
                  label="Occupation"
                  value={member.Occupation ?? "-"}
                />
                <ViewControl
                  label="Member Level"
                  value={member.MemberLevelDescription ?? "-"}
                />
                <ViewControl
                  label="Membership Type"
                  value={member.MembershipTypeDescription ?? "-"}
                />
                <ViewControl
                  label="Date Joined"
                  value={
                    member.DateJoined
                      ? DateUtil.convertUtcToLocalTime(member.DateJoined)
                      : "-"
                  }
                />
                <ViewControl
                  label="Date Financial"
                  value={
                    member.DateFinancial
                      ? DateUtil.convertUtcToLocalTime(member.DateFinancial)
                      : "-"
                  }
                />
              </VStack>
              <Accordion allowMultiple allowToggle w="100%" variant="section">
                {member.HomeContactDetails && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Home Contact Details
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Phone Number"
                              value={
                                member.HomeContactDetails?.PhoneNumber ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accepts Phone Marketing"
                              value={
                                member.HomeContactDetails?.AcceptsPhoneMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Mobile Number"
                              value={
                                member.HomeContactDetails?.MobileNumber ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accept Mobile Marketing"
                              value={
                                member.HomeContactDetails
                                  ?.AcceptsMobileMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Email Address"
                              value={
                                member.HomeContactDetails?.EmailAddress ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accepts Email Marketing"
                              value={
                                member.HomeContactDetails?.AcceptsEmailMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                {member.WorkContactDetails && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Work Contact Details
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Phone Number"
                              value={
                                member.WorkContactDetails?.PhoneNumber ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accepts Phone Marketing"
                              value={
                                member.WorkContactDetails?.AcceptsPhoneMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Mobile Number"
                              value={
                                member.WorkContactDetails?.MobileNumber ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accept Mobile Marketing"
                              value={
                                member.WorkContactDetails
                                  ?.AcceptsMobileMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Email Address"
                              value={
                                member.WorkContactDetails?.EmailAddress ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accepts Email Marketing"
                              value={
                                member.WorkContactDetails?.AcceptsEmailMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                {member.PostalContactDetails && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Postal Contact Details
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Phone Number"
                              value={
                                member.PostalContactDetails?.PhoneNumber ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accepts Phone Marketing"
                              value={
                                member.PostalContactDetails
                                  ?.AcceptsPhoneMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Mobile Number"
                              value={
                                member.PostalContactDetails?.MobileNumber ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accept Mobile Marketing"
                              value={
                                member.PostalContactDetails
                                  ?.AcceptsMobileMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Email Address"
                              value={
                                member.PostalContactDetails?.EmailAddress ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Accepts Email Marketing"
                              value={
                                member.PostalContactDetails
                                  ?.AcceptsEmailMarketing
                                  ? "Yes"
                                  : "No"
                              }
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                {member.HomeAddress && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Home Address
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Address Line 1"
                              value={member.HomeAddress?.AddressLine1 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Address Line 2"
                              value={member.HomeAddress?.AddressLine2 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Address Line 3"
                              value={member.HomeAddress?.AddressLine3 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Suburb"
                              value={member.HomeAddress?.Suburb ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="State"
                              value={member.HomeAddress?.State ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Post Code"
                              value={member.HomeAddress?.PostCode ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Country"
                              value={member.HomeAddress?.Country ?? "-"}
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                {member.WorkAddress && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Work Address
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Address Line 1"
                              value={member.WorkAddress?.AddressLine1 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Address Line 2"
                              value={member.WorkAddress?.AddressLine2 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Address Line 3"
                              value={member.WorkAddress?.AddressLine3 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Suburb"
                              value={member.WorkAddress?.Suburb ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="State"
                              value={member.WorkAddress?.State ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Post Code"
                              value={member.WorkAddress?.PostCode ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Country"
                              value={member.WorkAddress?.Country ?? "-"}
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                {member.PostalAddress && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left" fontWeight="bold">
                              Postal Address
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Address Line 1"
                              value={member.PostalAddress?.AddressLine1 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Address Line 2"
                              value={member.PostalAddress?.AddressLine2 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Address Line 3"
                              value={member.PostalAddress?.AddressLine3 ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Suburb"
                              value={member.PostalAddress?.Suburb ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="State"
                              value={member.PostalAddress?.State ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Post Code"
                              value={member.PostalAddress?.PostCode ?? "-"}
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Country"
                              value={member.PostalAddress?.Country ?? "-"}
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
              </Accordion>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
